import {
  add,
  addMonths,
  format,
  formatDistanceToNow,
  formatISO,
  isEqual,
  isSameDay,
  isYesterday,
  parseISO,
  subDays,
  subMonths,
} from 'date-fns';
import {State} from '@synako/halitrek';
import {LigneDePanierDesc} from 'api-types/ffconnect-hal';

export const getTempsRestant = (timestamp: string | number) => {
  return formatDistanceToNow(new Date(timestamp));
};

export const dateAvecSlashes = (timestamp: string | number | undefined) => {
  return format(timestamp ? new Date(timestamp) : new Date(), 'dd/MM/yyyy');
};

export const dateEnTexte = (timestamp: string | number | undefined) => {
  return format(timestamp ? new Date(timestamp) : new Date(), 'do MMMM yyyy');
};

/**
 * Formate une plage glissante de jours (exprimée en nombre de jours avant
 * aujourd'hui) sous la forme « dd/MM - dd/MM », dans la locale date-fns courante
 * (fr/en/de). Sert aux tooltips « X derniers jours » : statut d'activité FFConnect
 * et tendances du cadencier. `offsetFin` à 0 = aujourd'hui.
 * Ex. : formaterPlageJours(30) → « 23/05 - 22/06 » ; formaterPlageJours(60, 30)
 * → la période des 30 jours d'avant.
 */
export const formaterPlageJours = (offsetDebut: number, offsetFin = 0) => {
  const maintenant = new Date();
  const debut = subDays(maintenant, offsetDebut);
  const fin = subDays(maintenant, offsetFin);
  return `${format(debut, 'dd/MM')} - ${format(fin, 'dd/MM')}`;
};

export const isDateDepassee = (
  timestampDispo: string | number | undefined,
  precommande: boolean,
) => {
  if (!precommande && timestampDispo) {
    const today = new Date();
    const tomorrow = formatISO(add(today, {days: 1}), {representation: 'date'});
    const dateDispo = formatISO(timestampDispo, {representation: 'date'});
    return dateDispo <= tomorrow;
  }
  return false;
};

export const isDateEqual = (d1: Date, d2: Date) => {
  return isEqual(format(d1, 'yyyy-MM-dd'), format(d2, 'yyyy-MM-dd'));
};

export const isDateStringAfterOrEqual = (d1: string, d2: string): boolean => {
  return parseISO(d1) >= parseISO(d2);
};

export const moisAnneeEnTexte = (
  dateLivraison: string,
  monthOffset: number,
) => {
  const formated = format(addMonths(dateLivraison, monthOffset), 'MMMM yyyy');
  return `${formated.substring(0, 1).toUpperCase()}${formated.substring(1)}`;
};

export const jourMoisEnTexte = (dateLivraison: string, year = false) => {
  return format(dateLivraison, year ? 'do MMMM yyyy' : 'do MMMM');
};

/**
 * Retourne un mapping des noms d'affichage pour les 12 derniers mois
 * @returns Un objet associant les IDs de mois (1-12) aux infos d'affichage
 */
export const getDerniersDouzesMois = () => {
  const today = new Date();
  const mapping: Record<
    string,
    {monthNumber: number; year: number; displayName: string}
  > = {};

  // Générer le mapping pour les 12 derniers mois
  for (let i = 0; i < 12; i++) {
    // Nous commençons par le mois le plus ancien (il y a 12 mois) jusqu'au mois actuel
    const date = subMonths(today, 12 - i);
    const monthNumber = date.getMonth() + 1; // 1-12
    const year = date.getFullYear();

    // L'ID de mois correspond à sa position (1-12) dans notre liste de 12 mois
    const monthId = (i + 1).toString(); // 1-12

    mapping[monthId] = {
      monthNumber, // Le vrai numéro du mois (1-12)
      year,
      displayName: format(date.toISOString(), 'MMMM yyyy'),
    };
  }

  return mapping;
};

/**
 * Formate une date pour l'affichage dans les composants
 * @param date Date à formater ou string ISO
 * @param dateFormat Format de la date (par défaut: 'dd MMM yyyy')
 * @param fallback Valeur de remplacement si la date est nulle
 * @returns Date formatée en texte
 */
export const formatDateForDisplay = (
  date: Date | string | null,
  dateFormat = 'dd MMM yyyy',
  fallback = '',
): string => {
  if (!date) return fallback;

  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, dateFormat);
};

/**
 * Calcule le décalage de mois entre deux dates
 * @param date1 Première date (format ISO string ou Date)
 * @param date2 Deuxième date (par défaut: date actuelle)
 * @returns Nombre de mois de différence
 */
export const calculateMonthDifference = (
  date1?: string | Date | null,
  date2: Date = new Date(),
): number => {
  if (!date1) return 0;

  const d1 = typeof date1 === 'string' ? parseISO(date1) : date1;

  return (
    (d1.getFullYear() - date2.getFullYear()) * 12 +
    (d1.getMonth() - date2.getMonth())
  );
};

/**
 * Calcule le décalage de mois pour afficher une date spécifique ou par défaut le même mois de l'année dernière
 * Utilisé dans la modale plage de dates des listes saisonnières
 * @param targetDate Date cible (optionnel)
 * @returns Décalage de mois pour le calendrier
 */
export const getDefaultMonthOffset = (
  targetDate?: string | Date | null,
): number => {
  if (!targetDate) {
    // Afficher le même mois de l'année dernière par défaut
    return -12;
  }

  return calculateMonthDifference(targetDate);
};

/**
 * Tri les lignes de panier par date de création en ordre décroissant (plus récentes en premier)
 * Utilisé pour l'affichage UX côté client, tout en gardant l'ordre ASC côté serveur pour la stabilité du cache
 */
export const sortLignesDePanierParDate = (
  lignesDePanier: State<LigneDePanierDesc>[],
): State<LigneDePanierDesc>[] => {
  return [...lignesDePanier].sort((a, b) => {
    // Tri décroissant : b avant a pour avoir les plus récentes en premier
    return b.data.creee_le.localeCompare(a.data.creee_le);
  });
};

/**
 * Formate une date pour l'historique avec gestion du "Aujourd'hui"
 * @param date Date à formater (string ISO ou Date)
 * @returns Date formatée pour l'historique (ex: "Aujourd'hui à 14:30" ou "10 juillet 2025 à 14:30")
 */
export const formatDateForHistorique = (date: string | Date): string => {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  const today = new Date();

  // Vérifier si c'est aujourd'hui
  if (isSameDay(dateObj, today)) {
    return `Aujourd'hui à ${format(dateObj, 'HH:mm')}`;
  }

  // Sinon afficher la date complète
  return `${format(dateObj, 'do MMMM yyyy')} à ${format(dateObj, 'HH:mm')}`;
};

/**
 * Formate une date ISO pour un input datetime-local HTML
 * @param isoDate Date ISO string à formater
 * @param fallback Valeur de remplacement si la date est invalide
 * @returns Date formatée au format YYYY-MM-DDTHH:mm pour datetime-local
 */
export const formatDateForDatetimeLocal = (
  isoDate: string,
  fallback = '',
): string => {
  if (!isoDate) return fallback;

  try {
    const dateObj = parseISO(isoDate);

    // Vérifie que la date est valide
    if (isNaN(dateObj.getTime())) {
      return fallback;
    }

    // Format datetime-local: YYYY-MM-DDTHH:mm (sans secondes ni timezone)
    return format(dateObj, "yyyy-MM-dd'T'HH:mm");
  } catch (_error) {
    return fallback;
  }
};

/**
 * Formate une date pour l'affichage dans les conversations
 * - Aujourd'hui : "14:30"
 * - Hier : "Hier"
 * - Autre : "12 octobre"
 * @param date Date à formater (string ISO ou Date)
 * @param hierLabel Label pour "Hier" (pour i18n)
 * @returns Date formatée
 */
export const formatDateForConversation = (
  date: string | Date,
  hierLabel = 'Hier',
): string => {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  const today = new Date();

  if (isSameDay(dateObj, today)) {
    return format(dateObj, 'HH:mm');
  }

  if (isYesterday(dateObj)) {
    return hierLabel;
  }

  return format(dateObj, 'd MMMM');
};

/** Génère les noms de mois abrégés localisés via date-fns (locale globale) */
export function getMoisLabels(): string[] {
  return Array.from({length: 12}, (_, i) => {
    const label = format(new Date(2024, i, 1), 'MMM');
    return label.charAt(0).toUpperCase() + label.slice(1);
  });
}

// Formate le message de maintenance avec la timezone de l'utilisateur
export const formatMaintenanceMessage = (
  message: string,
  commenceLe: string,
  termineLe: string,
  language: string,
): string => {
  const debut = parseISO(commenceLe);
  const fin = parseISO(termineLe);

  const dateFormatee = format(debut, 'd MMMM yyyy');

  // Formate les heures selon la locale
  let heureDebut: string;
  let heureFin: string;

  if (language.startsWith('fr')) {
    // Format français: 14h30
    heureDebut = format(debut, "HH'h'mm");
    heureFin = format(fin, "HH'h'mm");
  } else if (language.startsWith('de')) {
    // Format allemand: 14:30
    heureDebut = format(debut, 'HH:mm');
    heureFin = format(fin, 'HH:mm');
  } else {
    // Format anglais: 2:30 PM
    heureDebut = format(debut, 'h:mm a');
    heureFin = format(fin, 'h:mm a');
  }

  const horaire = `${heureDebut} - ${heureFin}`;
  return `${message} (${dateFormatee}, ${horaire})`;
};
